/**
 * CardShell.tsx
 * カード共通の外枠。絵・カード名・今の設定を固定サイズで描画する。
 * 接続ハンドルとポートラベルは各カードのビューが children として渡す。
 */
import type { ReactNode } from 'react';
import { Wrench } from 'lucide-react';
import type { NodeKind } from '../types';
import { getNodeCategoryStyle } from '../nodeCategories';
import { getSettings } from '../nodeRegistry';

/**
 * カードの配色。
 * Tailwind はクラス名を静的に解析するため、文字列を組み立てず定数表で持つ。
 */
const ACCENTS = {
  emerald: { border: 'border-emerald-500', title: 'text-emerald-700', summary: 'text-emerald-600' },
  sky: { border: 'border-sky-500', title: 'text-sky-700', summary: 'text-sky-600' },
  violet: { border: 'border-violet-500', title: 'text-violet-700', summary: 'text-violet-600' },
  cyan: { border: 'border-cyan-500', title: 'text-cyan-700', summary: 'text-cyan-600' },
  lime: { border: 'border-lime-500', title: 'text-lime-700', summary: 'text-lime-600' },
  indigo: { border: 'border-indigo-500', title: 'text-indigo-700', summary: 'text-indigo-600' },
  amber: { border: 'border-amber-500', title: 'text-amber-700', summary: 'text-amber-600' },
  rose: { border: 'border-rose-500', title: 'text-rose-700', summary: 'text-rose-600' },
  slate: { border: 'border-slate-500', title: 'text-slate-700', summary: 'text-slate-600' },
  blue: { border: 'border-blue-500', title: 'text-blue-700', summary: 'text-blue-600' },
  red: { border: 'border-red-500', title: 'text-red-700', summary: 'text-red-600' },
  teal: { border: 'border-teal-500', title: 'text-teal-700', summary: 'text-teal-600' },
  orange: { border: 'border-orange-500', title: 'text-orange-700', summary: 'text-orange-600' },
  yellow: { border: 'border-yellow-500', title: 'text-yellow-700', summary: 'text-yellow-600' },
} as const;

export type CardAccent = keyof typeof ACCENTS;

type CardShellProps = {
  kind: NodeKind;
  accent: CardAccent;
  /** カード名（表示言語で切り替え済みの文字列） */
  title: string;
  /** 今の設定。未設定なら空文字を渡す（高さを揃えるため行自体は常に描画する） */
  summary: string;
  /** 設定値と一緒に表示する色見本。 */
  summaryColor?: string;
  children?: ReactNode;
};

export function CardShell({ kind, accent, title, summary, summaryColor, children }: CardShellProps) {
  const tone = getNodeCategoryStyle(kind) ?? ACCENTS[accent];
  const hasSettings = Boolean(getSettings(kind));

  return (
    <div
      className={`flow-node flow-node--card node-drag-handle relative flex flex-col items-center justify-center rounded-xl border-2 bg-white shadow-sm cursor-grab active:cursor-grabbing ${tone.border}`}
    >
      {/* 絵。カード名が隣にあるため読み上げ対象からは外す */}
      <img
        src={`/images/flow-cards/${kind}.png`}
        alt=""
        aria-hidden="true"
        draggable={false}
        onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
        className="flow-node__icon shrink-0 object-contain"
      />
      <div className={`flow-node__title w-full shrink-0 truncate text-center font-bold ${tone.title}`}>
        {title}
      </div>
      <div className={`flow-node__summary flex min-h-7 w-full shrink-0 items-center justify-center text-center ${tone.summary}`} title={summary}>
        <span className="inline-flex max-w-full items-center justify-center gap-1.5">
          {summaryColor && (
            <span
              aria-hidden="true"
              className="size-3 shrink-0 rounded-full border border-slate-300"
              style={{ backgroundColor: summaryColor }}
            />
          )}
          <span className="truncate">{summary}</span>
        </span>
      </div>
      {hasSettings && (
        <button
          type="button"
          data-node-settings
          aria-label={`${title}の設定`}
          title={`${title}の設定`}
          className="nodrag nopan absolute top-0 left-0 flex size-[45px] cursor-pointer items-start justify-start pl-2 pt-2 rounded-tl-xl rounded-br-[60px] text-slate-600 hover:bg-[#33333333] hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
        >
          <Wrench size={22} aria-hidden="true" />
        </button>
      )}
      {children}
    </div>
  );
}
